import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface RateLimitState {
  // Upload rate limit
  lastUploadTime: number | null;
  uploadCooldownSeconds: number;

  // Like rate limit
  likeRateLimitedUntil: number | null;

  // Search rate limit
  searchRateLimitedUntil: number | null;

  // Actions
  recordUpload: () => void;
  canUpload: () => boolean;
  getUploadCooldown: () => number;

  setLikeRateLimited: (retryAfter: number) => void;
  canLike: () => boolean;

  setSearchRateLimited: (retryAfter: number) => void;
  canSearch: () => boolean;

  handleRateLimitResponse: (endpoint: string, retryAfter: number) => void;
}

const UPLOAD_COOLDOWN = 60; // 60 seconds between uploads

export const useRateLimitStore = create<RateLimitState>()(
  persist(
    (set, get) => ({
      lastUploadTime: null,
      uploadCooldownSeconds: UPLOAD_COOLDOWN,
      likeRateLimitedUntil: null,
      searchRateLimitedUntil: null,

      recordUpload: () => {
        set({ lastUploadTime: Date.now() });
      },

      canUpload: () => {
        const { lastUploadTime, uploadCooldownSeconds } = get();
        if (!lastUploadTime) return true;
        const elapsed = (Date.now() - lastUploadTime) / 1000;
        return elapsed >= uploadCooldownSeconds;
      },

      getUploadCooldown: () => {
        const { lastUploadTime, uploadCooldownSeconds } = get();
        if (!lastUploadTime) return 0;
        const elapsed = (Date.now() - lastUploadTime) / 1000;
        const remaining = uploadCooldownSeconds - elapsed;
        return Math.max(0, Math.ceil(remaining));
      },

      setLikeRateLimited: (retryAfter: number) => {
        set({ likeRateLimitedUntil: Date.now() + retryAfter * 1000 });
      },

      canLike: () => {
        const { likeRateLimitedUntil } = get();
        if (!likeRateLimitedUntil) return true;
        return Date.now() >= likeRateLimitedUntil;
      },

      setSearchRateLimited: (retryAfter: number) => {
        set({ searchRateLimitedUntil: Date.now() + retryAfter * 1000 });
      },

      canSearch: () => {
        const { searchRateLimitedUntil } = get();
        if (!searchRateLimitedUntil) return true;
        return Date.now() >= searchRateLimitedUntil;
      },

      handleRateLimitResponse: (endpoint: string, retryAfter: number) => {
        if (endpoint.includes('/blahblahs') && !endpoint.includes('/like')) {
          // Upload rate limit - set lastUploadTime to trigger countdown
          set({ lastUploadTime: Date.now() - (UPLOAD_COOLDOWN - retryAfter) * 1000 });
        } else if (endpoint.includes('/like')) {
          get().setLikeRateLimited(retryAfter);
        } else if (endpoint.includes('/search') || endpoint.includes('/tags/suggest')) {
          get().setSearchRateLimited(retryAfter);
        }
      },
    }),
    {
      name: 'blahblah-rate-limit',
      partialize: (state) => ({
        lastUploadTime: state.lastUploadTime,
      }),
    }
  )
);
